import { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { showConflicts } from "../../hooks/draft";
import { describeTheirs } from "../../hooks/draft/diff";
import { useClipboardCopy } from "../../hooks/useClipboard";
import useUserFields from "../../hooks/useUserFields";
import {
  OWN_REBASE,
  OWN_RELOAD,
  RebaseModal,
  clipOf,
  isDeleted,
  isTaken,
  ownSentence,
  rebaseFailure,
  rebasePlan,
  takenSentence,
  titleOf as rowTitleOf,
  when
} from "./conflictDialog";
import styles from "./styles.module.scss";

const SUMMARY = 3;

// My values now: `mine` may be a function (read only when asked: the form's
// values change at every keystroke, nothing here runs for them)
const read = (mine) => (typeof mine === "function" ? mine() : mine);

// The summary of their changes (theirs against base): computed once per row
// and server version, never for my values
function useTheirSummary(conflict, versions, titleOf) {
  const { accessor, id, storedVersion, lastSaved } = conflict ?? {};
  const base = versions?.base;
  const theirs = versions?.theirs;
  const known = Boolean(conflict && versions && theirs);
  // The versions may be new objects at each render (the caller's
  // conflictVersions): the summary follows the version they have
  const signature = known
    ? `${accessor}:${id}:${storedVersion}:${lastSaved?.date ?? ""}`
    : null;

  return useMemo(
    () =>
      signature
        ? describeTheirs({
            base,
            theirs,
            accessor,
            limit: SUMMARY,
            titleOf
          })
        : null,
    // base and theirs are read for this signature only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature, titleOf]
  );
}

// A colleague saved (or deleted) the row of an open form while it had
// unsaved changes: the form is held on the version it was opened on (the
// store keeps their row out of it), its Save is refused. The banner, in
// the form's header, says who and when, sums up what they changed, and
// offers:
// - Reload: onReload() (their version; my changes to the form are lost);
// - Show changes: onShowChanges() if given, else the conflict dialog with
//   this conflict (Discard: onReload, Rebase: onRebase, Keep editing:
//   onKeepEditing, my values: mine);
// - Apply mine on theirs (onRebase given, not on main, not deleted): merges
//   my values of now on their version; a preview, then Confirm calls
//   onRebase(items), as the dialog does (items: rebasePlan's, one here:
//   [{ accessor, id, title, row, version, base, theirs, mine }], row the
//   merged row), so the same handler serves both. When it can't (a field
//   changed by both), the window says why;
// - Copy my values: onCopy() if given, else the clipboard format of the
//   Item's Copy with my values.
// conflict: formConflictOf's ({ accessor, id, action, storedVersion,
// lastSaved, reason? }); versions: conflictVersions' ({ base, theirs }),
// also given to the dialog of Show changes (versionsOf); subject: what the
// row is in the sentence ("this item" by default, e.g. "the endpoint home");
// mine: my row, or a function that reads it (better: the form's values are
// read only when a button asks for them).
export default function ConflictBanner({
  conflict,
  versions,
  mine,
  onReload,
  onKeepEditing,
  onRebase,
  onCopy,
  onShowChanges,
  titleOf = rowTitleOf,
  subject = "this item",
  className = ""
}) {
  const { branch, tenant } = useUserFields("branch", "tenant");
  const { copy } = useClipboardCopy();
  const summary = useTheirSummary(conflict, versions, titleOf);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const accessor = conflict?.accessor ?? conflict?.type;
  const id = conflict?.id;
  const mineOf = useCallback(
    (a, i) => (a === accessor && i === id ? read(mine) : undefined),
    [accessor, id, mine]
  );
  // The dialog reads the versions through the store (conflictVersions);
  // ours come from the caller
  const versionsOf = useCallback(
    (a, i) => (a === accessor && i === id ? versions : undefined),
    [accessor, id, versions]
  );

  const showChanges = useCallback(() => {
    if (onShowChanges) return onShowChanges();
    showConflicts({
      conflicts: [{ type: accessor, ...conflict, form: true }],
      onDiscard: onReload,
      onRebase,
      onKeepEditing,
      mineOf,
      versionsOf
    });
  }, [
    accessor,
    conflict,
    mineOf,
    onKeepEditing,
    onRebase,
    onReload,
    onShowChanges,
    versionsOf
  ]);

  const askRebase = useCallback(() => {
    setFailure(null);
    setPlan(rebasePlan([{ type: accessor, ...conflict }], mineOf, versionsOf));
  }, [accessor, conflict, mineOf, versionsOf]);

  const confirmRebase = useCallback(
    (items) => {
      setBusy(true);
      setFailure(null);
      Promise.resolve(onRebase(items))
        .then((result) => {
          const refused = rebaseFailure(result);
          if (refused) setFailure(refused);
          else setPlan(null);
        })
        .catch((error) => {
          console.error("⚠️ REBASE OF THE FORM", error);
          setFailure(String(error?.message ?? error));
        })
        .finally(() => setBusy(false));
    },
    [onRebase]
  );

  const copyMine = useCallback(() => {
    if (onCopy) return onCopy();
    const row = read(mine);
    if (row) copy(clipOf([{ accessor, row }], tenant));
  }, [accessor, copy, mine, onCopy, tenant]);

  if (!conflict) return null;

  const deleted = isDeleted(conflict);
  const taken = isTaken(conflict);
  // Our own save come back under the form, edited on an older version
  // shown meanwhile (the store's own: OUR OWN SAVES): not a colleague's
  const own = Boolean(conflict.own) && !deleted && !taken;
  const who = conflict.lastSaved?.username ?? "Someone";
  const on = conflict.lastSaved?.date
    ? ` on ${when(conflict.lastSaved.date)}`
    : "";
  const canRebase =
    Boolean(onRebase) && branch !== "main" && !deleted && !taken;
  const lines = summary?.lines ?? [];

  return (
    <div className={`${styles.banner} ${className}`} role="alert">
      <span className={styles.bannerText}>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className={styles.conflictIcon}
        />
        {taken
          ? takenSentence(conflict)
          : deleted
            ? `${who === "Someone" ? "A colleague" : who} deleted ${subject}${on}: your save will be refused.`
            : own
              ? ownSentence(conflict)
              : `${who} saved ${subject}${on}: your save will be refused.`}
      </span>
      {lines.length ? (
        <span className={styles.bannerSummary}>
          {lines.map((line) => line.label).join(" · ")}
          {summary.more ? ` +${summary.more}` : ""}
        </span>
      ) : null}
      <span className={styles.bannerButtons}>
        {onReload ? (
          <Button size="sm" variant="outline-warning" onClick={onReload}>
            {own ? OWN_RELOAD : "Reload"}
          </Button>
        ) : null}
        {taken ? null : (
          <Button size="sm" variant="outline-secondary" onClick={showChanges}>
            Show changes
          </Button>
        )}
        {canRebase ? (
          <Button size="sm" variant="outline-primary" onClick={askRebase}>
            {own ? OWN_REBASE : "Apply mine on theirs"}
          </Button>
        ) : null}
        <Button size="sm" variant="outline-secondary" onClick={copyMine}>
          Copy my values
        </Button>
      </span>
      <RebaseModal
        plan={plan}
        busy={busy}
        failure={failure}
        own={own}
        onConfirm={confirmRebase}
        onCancel={() => setPlan(null)}
      />
    </div>
  );
}
